import { useState } from "react";
import {
  useFloating,
  useClick,
  useInteractions,
  autoUpdate,
  shift,
  useRole,
  useDismiss,
  FloatingPortal,
  offset,
  flip,
  FloatingFocusManager,
} from "@floating-ui/react";
import { useNavigate } from "react-router-dom";
import cn from "classnames";

// components
import { RoundedButton } from "components/LibraryComponents/Components";

// generic
import { Generic } from "types/filters-store.types";

// styles
import styles from "./ChooseDelimiter.module.scss";

export const DelimiterList: Generic[] = [
  {
    label: "Comma (,)",
    value: "comma",
  },
  {
    label: "Semicolon (;)",
    value: "semicolon",
  },
];

type ChooseDelimiterProps = {
  selectedOption: string;
  handleOption: (value: string) => void;
};

export default function ChooseDelimiter({
  selectedOption,
  handleOption,
}: ChooseDelimiterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement: "bottom-end",
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { bubbles: true });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  const handleChange = (item: Generic) => {
    handleOption(item.value);
    setIsOpen(false);
  };

  const currentOption = selectedOption
    ? selectedOption === ","
      ? "comma"
      : "semicolon"
    : "";

  return (
    <>
      <div className={styles["choose-delimiter-root"]}>
        <RoundedButton
          variant="secondary"
          className={cn(styles["dropdown-btn"], { [styles["active"]]: isOpen })}
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          Choose delimiter
        </RoundedButton>

        <FloatingPortal id="choose-delimiter">
          <FloatingFocusManager context={context} modal={false} guards={false}>
            {isOpen && (
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, outline: "none" }}
                {...getFloatingProps()}
                className={styles["content"]}
              >
                {/* this div is required to make the focus trap work */}
                <div tabIndex={0} style={{ outline: "none" }} />

                <ul className={styles["list"]}>
                  {DelimiterList.map((item, index) => (
                    <li
                      key={index}
                      className={cn(styles["list-item"], {
                        [styles["active"]]: currentOption === item.value,
                      })}
                      onClick={() => handleChange(item)}
                    >
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </FloatingFocusManager>
        </FloatingPortal>
      </div>
    </>
  );
}
